import React from 'react';
import { Inbox, UserCheck, Footprints, CheckCircle2, XCircle } from 'lucide-react';

/**
 * JobStatusStepper — subtle animated progress tracker for a single job.
 * Received → Assigned → Attempting → Served (or "Unable to Serve").
 */
const JobStatusStepper = ({ job }) => {
  if (!job) return null;

  const served = job.service_status === 'Served' || (!job.service_status && !!job.served_at);
  const hasAttempts = (Array.isArray(job.attempts) && job.attempts.length > 0) || (job.attempt_count > 0);
  const assigned = !!job.assigned_server || hasAttempts || served;
  const nonService = !served && !!job.archived_at && hasAttempts; // worked but couldn't complete

  const steps = [
    { key: 'received', label: 'Received', Icon: Inbox, done: true },
    { key: 'assigned', label: 'Assigned', Icon: UserCheck, done: assigned },
    { key: 'attempting', label: 'Attempting', Icon: Footprints, done: hasAttempts || served },
    {
      key: 'served',
      label: nonService ? 'Unable to Serve' : 'Served',
      Icon: nonService ? XCircle : CheckCircle2,
      done: served,
      bad: nonService,
    },
  ];

  const servedIdx = steps.length - 1;
  let currentIdx = steps.findIndex((s) => !s.done);
  if (currentIdx === -1) currentIdx = servedIdx;
  // Keep the pulse parked on 'Attempting' until the job is actually served
  // (or ended as 'Unable to Serve') — don't promote the terminal 'Served'
  // step to "current" just because every step before it is done.
  if (!served && !nonService && currentIdx === servedIdx) currentIdx = servedIdx - 1;

  const RED = 'var(--pv2-red-bright)';
  const GREEN = 'var(--pv2-green-text)';
  const MUTED = 'var(--pv2-text-subtle)';

  return (
    <div className="pv2-stepper" data-testid="pv2-job-stepper">
      {steps.map((s, i) => {
        const { Icon } = s;
        const isCurrent = i === currentIdx && !served;
        const active = s.done || isCurrent;
        let color = MUTED;
        if (s.bad) color = RED;
        else if (s.key === 'served' && served) color = GREEN;
        else if (active) color = RED;

        return (
          <React.Fragment key={s.key}>
            <div className="pv2-step" style={{ opacity: active ? 1 : 0.55 }} data-testid={`pv2-step-${s.key}`}>
              <div className={`pv2-step-dot${isCurrent ? ' pv2-step-pulse' : ''}`} style={{ borderColor: color, color }}>
                <Icon size={15} strokeWidth={1.75} />
              </div>
              <div className="pv2-step-label" style={{ color: active ? color : MUTED }}>{s.label}</div>
            </div>
            {i < steps.length - 1 && (
              <div
                className="pv2-step-line"
                style={{ background: (steps[i + 1].done || (i + 1 === servedIdx && nonService)) ? RED : 'var(--pv2-ink-line)' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default JobStatusStepper;
